import type { MiddlemanRoute, ReviewDecision } from "./types";

export const LEVELS = [
  { level: 0, title: "Unplaced", xp: 0, unlock: "Complete the Entry Check" },
  { level: 1, title: "Recruit", xp: 50, unlock: "Choose a route and read SOPs" },
  { level: 2, title: "Runner", xp: 200, unlock: "Start proving tasks" },
  { level: 3, title: "Proven", xp: 500, unlock: "Complete the live drill" },
  { level: 4, title: "Operative", xp: 1_200, unlock: "Claim paid tasks" },
  { level: 5, title: "Specialist", xp: 3_000, unlock: "Higher-value tasks and affiliate link" },
  { level: 6, title: "Reviewer", xp: 6_000, unlock: "Review Level 3-5 work" },
  { level: 7, title: "Lead", xp: 12_000, unlock: "Manage a cell of operatives" },
  { level: 8, title: "Manager", xp: 25_000, unlock: "Run a route cell" },
  { level: 9, title: "Director", xp: 50_000, unlock: "Strategy room and downstream share" },
  { level: 10, title: "Architect", xp: 100_000, unlock: "Inner Circle" },
] as const;

export const XP_ACTIONS = {
  entry_check: 50,
  choose_route: 25,
  proving_mission: 75,
  paid_mission: 150,
  high_value_mission: 300,
  accurate_review: 100,
  catch_bad_work: 200,
  recruit_reaches_level_4: 500,
  weekly_leaderboard: 250,
} as const;

export const ROUTES: Array<{
  id: Exclude<MiddlemanRoute, "unplaced">;
  icon: string;
  name: string;
  moves: string;
  description: string;
  outcome: string;
}> = [
  { id: "clipper", icon: "✂", name: "Clipper", moves: "Moves content", description: "Turn long videos and ideas into short clips, carousels, and scripts.", outcome: "Views" },
  { id: "seeder", icon: "↗", name: "Seeder", moves: "Moves links", description: "Place useful links where the right audience already spends time.", outcome: "Conversions" },
  { id: "closer", icon: "☎", name: "Closer", moves: "Moves decisions", description: "Use DMs, Looms, follow-ups, and calls to book demos.", outcome: "Demo calls" },
  { id: "scout", icon: "⌕", name: "Scout", moves: "Moves targets", description: "Find and qualify the people Closers and Seeders should reach.", outcome: "Pipeline fuel" },
  { id: "promoter", icon: "◉", name: "Promoter", moves: "Moves attention", description: "Post and engage organically to grow reach and website traffic.", outcome: "Views + conversions" },
];

export function levelForXp(xp: number) {
  return [...LEVELS].reverse().find((item) => xp >= item.xp) ?? LEVELS[0];
}

export function xpProgress(xp: number) {
  const current = levelForXp(xp);
  const next = LEVELS[current.level + 1];
  if (!next) return { current, next: null, percent: 100, remaining: 0 };
  const earned = xp - current.xp;
  const span = next.xp - current.xp;
  return { current, next, percent: Math.max(0, Math.min(100, (earned / span) * 100)), remaining: next.xp - xp };
}

export function paidMissionAccess(level: number, trustScore: number) {
  if (trustScore < 30) return { allowed: false, reason: "Membership suspended: trust is below 30." };
  if (trustScore < 60) return { allowed: false, reason: "Paid tasks are locked until trust returns to 60." };
  if (level < 4) return { allowed: false, reason: "Complete all three proving tasks to unlock paid work." };
  return { allowed: true, reason: "" };
}

export function reviewTrustDelta(input: {
  decision: ReviewDecision;
  honeypot: boolean;
  expectedDecision?: ReviewDecision;
}) {
  if (!input.honeypot) return input.decision === "approved" ? 10 : 10;
  return input.decision === input.expectedDecision ? 10 : -25;
}

export function missionTrustDelta(approved: boolean) {
  return approved ? 5 : -15;
}

export function revenueSplit(value: number) {
  return {
    executor: value * 0.7,
    house: value * 0.25,
    sponsor: value * 0.03,
    reviewer: value * 0.02,
  };
}
